import React, { useContext, useEffect, useRef, useState } from 'react';
import { useEffectOnce, useLocation } from 'react-use';

import { CartContext } from '@repo/contexts/CartContext';
import {
  ProductContainerContext,
  ProductContainerProvider,
} from '@repo/contexts/ProductContainerContext';
import type { SanityGlobalProduct } from '@repo/models/gatsby-graphql-types';
import { useUiComponentsInternationalContext } from '@repo/static-translations';
import { Button } from '@repo/ui-components/shadcn/Button';
import Badge from '@repo/ui-components/tailwind/Badge';
import ColorSwatch from '@repo/ui-components/tailwind/ColorSwatch';
import Image from '@repo/ui-components/tailwind/Image';
import Link from '@repo/ui-components/tailwind/Link';
import ProductModal from '@repo/ui-components/tailwind/ProductModal';
import SimpleCartBtn from '@repo/ui-components/tailwind/SimpleCartBtn';
import OptionTag from '@repo/ui-components/tailwind/OptionTag';
import {
  filteredVariants,
  formatReviewSku,
  pushToDataLayer,
  shrinkProductImage,
  variantOptionsFilter,
  isShopInPathname,
} from '@repo/utils';

interface ProductCardProps {
  onClick?: () => void;
}

const ProductCard = ({ onClick }: Readonly<ProductCardProps>) => {
  const { addToCart } = useContext(CartContext);
  const {
    activeVariant,
    handleOptionChange,
    pricing,
    product,
    quantity,
    selectedOptions,
    selectedSellingPlan,
    sellingPlanGroups,
    variants,
  } = useContext(ProductContainerContext) as any;

  const location = useLocation();
  const cardRef = useRef();
  const { tt, localizePrice } = useUiComponentsInternationalContext();

  const [isAvailableForSale, setIsAvailableForSale] = useState(false);
  const [isProductModalOpen, setIsProductModalOpen] = useState(false);

  const productLifecycle = product?.shopifyProduct?.productLifecycle;

  const getVariantsMatchingAnySelectedOption = () => {
    const colorOptionName = product?.shopifyProduct?.options?.find(opt =>
      opt.name.toLowerCase().includes('color')
    )?.name;

    if (!selectedOptions || !variants?.length) {
      return [];
    }

    if (!colorOptionName) {
      return product?.shopifyProduct?.variants;
    }

    const matchingVariants = new Set();

    Object.entries(selectedOptions).forEach(([optionName, selectedValue]) => {
      variants.forEach(v => {
        const variantOptionValue = v.variantOptions?.find(
          o => o.name === optionName
        )?.value;

        if (variantOptionValue === selectedValue) {
          matchingVariants.add(v);
        }
      });
    });

    return Array.from(matchingVariants);
  };

  useEffect(() => {
    setIsAvailableForSale(activeVariant?.availableForSale);
  }, [activeVariant]);

  const variantsMatchingSelectedOptions =
    getVariantsMatchingAnySelectedOption();
  const availableVariantsMatchingSelectedOptions =
    variantsMatchingSelectedOptions?.filter(v => v.availableForSale === true);

  const isAvailable =
    (isAvailableForSale ||
      availableVariantsMatchingSelectedOptions?.length > 0) &&
    productLifecycle !== 'Obsolete' &&
    productLifecycle !== 'Coming Soon';

  const getLinkUrl = () => {
    const url = `/product/${product?.store?.slug?.current}`;

    const paramsBuilder = new URLSearchParams('');
    if (activeVariant) paramsBuilder.set('sku', activeVariant.sku);

    if (isShopInPathname(location?.pathname)) {
      // ! URIEncode so path or `?` will not cause page blips or unexpected errors on pdp
      paramsBuilder.set(
        'collection-location',
        `${encodeURIComponent(location?.pathname || '')}${encodeURIComponent(
          location?.search || ''
        )}`
      );
    }

    return `${url}${paramsBuilder.size ? `?${paramsBuilder.toString()}` : ''}`;
  };

  const handleDataLayer = event => {
    pushToDataLayer(event, {
      ecommerce: {
        currency: 'USD',
        value: pricing?.price,
      },
      items: [
        {
          variant: activeVariant,
          product: product?.shopifyProduct,
          category:
            location?.pathname?.startsWith('/shop') && location?.pathname,
        },
      ],
    });
  };

  const onSimpleCartBtnClick = async e => {
    /*
      ! This only handles single variant non subscription products,
      multi-variant or subscription products handled in modal with ProductOptions form submit
      in packages/@repo/ui-components/dept/molecules/ProductOptions/index.jsx
    */

    e.preventDefault();
    e.stopPropagation();

    await addToCart({
      activeVariant,
      product,
      quantity,
      selectedSellingPlan: null,
      variantId: activeVariant.shopifyId,
      variantMetafields: activeVariant?.metafields,
    });

    const GAItems = [
      {
        variant: activeVariant,
        product: product?.shopifyProduct,
        quantity,
        isSubscription: false,
        category: isShopInPathname(location?.pathname)
          ? location.pathname // collection page
          : decodeURIComponent(
              new URLSearchParams(location.search).get(
                // pdp page
                'collection-location'
              ) || ''
            ),
      },
    ];

    pushToDataLayer('add_to_cart', {
      ecommerce: {
        currency: 'USD',
        value: GAItems.reduce(
          (total, GAItem) => total + GAItem.quantity * GAItem.variant.price,
          0.0
        ),
      },
      items: GAItems,
    });
  };

  const renderButton = () => {
    if (productLifecycle === 'Coming Soon') {
      return (
        <Button disabled variant="ghost">
          {tt(s => s.productOptionsComponent.comingSoonButton)}
        </Button>
      );
    }

    if (productLifecycle === 'Obsolete') {
      return (
        <Button disabled variant="ghost">
          {tt(s => s.productOptionsComponent.discontinuedButton)}
        </Button>
      );
    }

    if (!isAvailable) {
      return (
        <Button disabled variant="ghost">
          {tt(s => s.productOptionsComponent.outOfStockButton)}
        </Button>
      );
    }

    if (variants.length > 1 || sellingPlanGroups?.length) {
      return (
        <SimpleCartBtn
          aria-label={`${tt(s => s.productOptionsComponent.addToCartButton)} ${
            product?.store?.title
          }`}
          disabled={!isAvailable}
          onClick={e => {
            e.preventDefault();
            e.stopPropagation();

            setIsProductModalOpen(true);
          }}
        />
      );
    }

    return (
      <SimpleCartBtn
        aria-label={`${tt(s => s.productOptionsComponent.addToCartButton)} ${
          product?.shopifyProduct?.title
        }`}
        disabled={!isAvailable}
        onClick={onSimpleCartBtnClick}
        title="Add to Cart"
      />
    );
  };

  const renderColorOption = () => {
    const colorOption = product.shopifyProduct?.options?.find(o =>
      o.name.toLowerCase().includes('color')
    );

    let colorOptions = [];

    if (colorOption?.values?.length > 1) {
      colorOptions = colorOption.values.map(cov => {
        const shopifyVariant = product.shopifyProduct?.variants?.find(v =>
          v.selectedOptions.some(
            so => so.name.toLowerCase().includes('color') && so.value === cov
          )
        );

        const sanityVariant = product.store?.variants?.find(
          v =>
            v.store?.sku === shopifyVariant?.sku ||
            v.sku === shopifyVariant?.sku
        );

        if (sanityVariant) {
          const sanityVariantMetafields =
            sanityVariant?.store?.metafields || sanityVariant?.metafields || [];
          const image = sanityVariantMetafields.find(mf =>
            ['swatch_image', 'swatch_hex'].includes(mf.name)
          );

          if (image) {
            return {
              image: image.value,
              type: image.name,
              value: cov,
              name: colorOption.name,
            };
          }
        }

        return {
          value: cov,
          type: 'string',
          name: colorOption.name,
        };
      });

      return (
        colorOption &&
        colorOptions.length > 1 && (
          <div className="flex flex-wrap gap-3" role="radiogroup">
            {colorOptions.map(v => {
              if (['swatch_image', 'swatch_hex'].includes(v.type)) {
                return (
                  <ColorSwatch
                    handleChange={handleOptionChange}
                    key={v?.value}
                    option={v}
                    selected={selectedOptions[v.name] === v.value}
                    small
                    unavailable={false}
                  />
                );
              }

              return (
                <OptionTag
                  handleOptionChange={handleOptionChange}
                  key={v.value}
                  option={colorOption}
                  selectedOptions={selectedOptions}
                  value={v.value}
                />
              );
            })}
          </div>
        )
      );
    }
    return null;
  };

  if (!product) return null;

  return (
    <>
      <Link
        aria-label={product.store.title}
        className="flex flex-col h-full w-full p-4 relative gap-3 items-start bg-card border border-border rounded-lg hover:border-cta"
        link={getLinkUrl()}
        onClick={onClick ?? (() => handleDataLayer('select_item'))}
        ref={cardRef}
      >
        {product.badge?.text && (
          <div className="absolute top-2 left-2 z-[2]">
            <Badge
              hex={product.badge.hex || null}
              message={product.badge.text || null}
            />
          </div>
        )}
        <Image
          alt={product.store.title}
          className="h-auto w-full"
          src={shrinkProductImage(
            activeVariant?.media.find(m => !!m.image?.src)?.image.src ||
              product.store.previewImageUrl,
            720
          )}
        />
        <div>
          <h2 className="text-base font-bold mb-0 overflow-visible text-foreground-headline">
            {product.store.title}
          </h2>
        </div>
        <div
          className="h-6 w-full mr-auto [&:svg]:!mr-6"
          data-bv-product-id={formatReviewSku(
            activeVariant?.sku ?? product?.shopifyProduct?.variants?.[0]?.sku
          )}
          data-bv-seo="false"
          data-bv-show="inline_rating"
          id={`BVRRInlineRating-${formatReviewSku(
            activeVariant?.sku ?? product?.shopifyProduct?.variants?.[0]?.sku
          )}`}
        />
        {!!product.dynamicFeatures?.length && (
          <ul className="my-0">
            {product.dynamicFeatures?.slice(0, 2).map(feature => (
              <li className="text-sm text-foreground" key={feature.text}>
                {feature.text}
              </li>
            ))}
          </ul>
        )}
        {productLifecycle !== 'Obsolete' &&
          productLifecycle !== 'Coming Soon' &&
          renderColorOption()}
        <div className="flex w-full mt-auto items-end justify-between">
          <div className="flex flex-col justify-end gap-3 items-start w-full">
            {!!sellingPlanGroups?.length && (
              <Badge
                discount={
                  selectedSellingPlan?.percentageDiscount ??
                  sellingPlanGroups[0].percentageDiscount
                }
                sub
              />
            )}
            <div className="flex justify-between items-end w-full">
              <div className="flex flex-col">
                {productLifecycle !== 'Obsolete' && (
                  <span className="text-xl font-bold">
                    {pricing?.getPriceRange()}
                  </span>
                )}
                {!!pricing?.compareAtPrice && (
                  <div className="flex flex-wrap gap-x-1">
                    <span className="font-light text-destructive line-through">
                      {localizePrice(pricing?.compareAtPrice)}
                    </span>
                    <span className="font-light text-destructive">
                      ({pricing?.percentageDiscount}% savings)
                    </span>
                  </div>
                )}
              </div>
              {renderButton()}
            </div>
          </div>
        </div>
      </Link>
      <ProductModal
        isOpen={isProductModalOpen}
        onClose={() => setIsProductModalOpen(false)}
        product={product}
      />
    </>
  );
};

interface ProductCardWrapperProps {
  onClick?: () => void;
  product: SanityGlobalProduct;
}

const ProductCardWrapper = ({
  onClick,
  product,
}: Readonly<ProductCardWrapperProps>) => {
  const [activeSku, setActiveSku] = useState(null);

  useEffectOnce(() => {
    const SKU = product.shopifyProduct?.variants?.[0]?.sku;
    setActiveSku(SKU);
  });

  if (product?.shopifyProduct?.variants.length > 1) {
    const filtered = filteredVariants(product?.shopifyProduct?.variants);
    const options = variantOptionsFilter(filtered);

    /* eslint-disable no-param-reassign */
    product.shopifyProduct.variants = filtered;
    product.shopifyProduct.options = options;

    // this hack resolved the variant lifecycle rendering a range of prices on the product card
    const prices = filtered.map(v => v.price);
    product.shopifyProduct.priceRangeV2.minVariantPrice.amount = Math.min(
      ...prices
    ).toString();
    product.shopifyProduct.priceRangeV2.maxVariantPrice.amount = Math.max(
      ...prices
    ).toString();
  }

  const onOptionChange = ({ variant }) => {
    if (variant?.sku) {
      setActiveSku(variant.sku);
    } else {
      setActiveSku(null);
    }
  };

  return (
    <ProductContainerProvider
      activeSku={activeSku}
      onOptionChange={onOptionChange}
      product={product}
    >
      <ProductCard onClick={onClick} />
    </ProductContainerProvider>
  );
};

export default ProductCardWrapper;
